import React, { useState } from 'react'
import { useQueryClient } from 'react-query'
import {
  Box,
  Flex,
  Text,
  Button as ChakraButton,
  Alert,
  AlertIcon,
  Spinner,
  FormControl,
  FormLabel,
  Input,
  useToast
} from '@chakra-ui/react'
import Axios from '../../../Axios'

// Match the existing application buttons, while forwarding the ref for popovers.
export const Button = React.forwardRef(({ children, ...props }, ref) => (
  <ChakraButton
    ref={ref}
    bg="green.500"
    color="white"
    borderRadius={8}
    _hover={{ bg: 'green.100' }}
    {...props}
  >
    {children}
  </ChakraButton>
))
export const currentMonth = () =>
  new Date()
    .toLocaleDateString('sv-SE', { timeZone: 'America/Santiago' })
    .slice(0, 7)
export const isMonth = (value) => /^20\d{2}-(0[1-9]|1[0-2])$/.test(value)
export const PeriodField = ({
  value,
  onChange,
  id = 'collection-period',
  disabled = false
}) => (
  <FormControl maxW="210px">
    <FormLabel htmlFor={id} color="green.500" fontSize="sm">
      Mes de cobro
    </FormLabel>
    <Input
      id={id}
      type="month"
      value={value}
      isDisabled={disabled}
      onChange={(e) => onChange(e.target.value)}
    />
  </FormControl>
)
export const billingRoles = [
  'ADMINISTRADOR',
  'FINANZAS',
  'DEPTO_CONTROL_GESTION_SOCIAL',
  'GERENCIA',
  'PRESIDENCIA',
  'REVISION'
]
export const writers = [
  'ADMINISTRADOR',
  'FINANZAS',
  'DEPTO_CONTROL_GESTION_SOCIAL'
]
export const money = (value) =>
  value == null
    ? 'Sin registro'
    : new Intl.NumberFormat('es-CL', {
        style: 'currency',
        currency: 'CLP',
        maximumFractionDigits: 0
      }).format(value)
export const labels = {
  PENDING: 'Pendiente de respuesta',
  PAID: 'Cobrado',
  PARTIAL: 'Cobro parcial',
  OVERPAID: 'Cobro mayor al esperado',
  REJECTED: 'No cobrado',
  UNEXPECTED: 'Sin esperado registrado'
}
export const colors = {
  PENDING: 'gray',
  PAID: 'green',
  PARTIAL: 'orange',
  OVERPAID: 'purple',
  REJECTED: 'red',
  UNEXPECTED: 'blue'
}
export const get = async (url, params) =>
  (await Axios.get(`/billing/${url}`, { params })).data
export const Loading = ({ query }) =>
  query.isLoading ? (
    <Flex p={6} gap={3}>
      <Spinner />
      <Text>Cargando información…</Text>
    </Flex>
  ) : query.isError ? (
    <Alert status="error">
      <AlertIcon />
      {query.error?.response?.data?.message ||
        'No se pudo cargar la información.'}
      <Button ml={3} onClick={() => query.refetch()}>
        Reintentar
      </Button>
    </Alert>
  ) : null
export const TableBox = ({ children }) => (
  <Box
    overflowX="auto"
    mt={4}
    sx={{
      '& thead': { bg: 'beige.500' },
      '& th': { color: 'green.500', whiteSpace: 'nowrap' },
      '& td': { fontSize: 'sm' }
    }}
  >
    {children}
  </Box>
)
export const Pager = ({ page, setPage, total = 0 }) => (
  <Flex justify="space-between" align="center" mt={4}>
    <Text fontSize="sm">
      {total.toLocaleString('es-CL')} registros · Página {page + 1}
    </Text>
    <Flex gap={2}>
      <Button size="sm" isDisabled={!page} onClick={() => setPage(page - 1)}>
        Anterior
      </Button>
      <Button
        size="sm"
        isDisabled={(page + 1) * 50 >= total}
        onClick={() => setPage(page + 1)}
      >
        Siguiente
      </Button>
    </Flex>
  </Flex>
)

export function useBillingAction() {
  const [busy, setBusy] = useState(false)
  const toast = useToast()
  const cache = useQueryClient()
  const action = async (fn, success) => {
    setBusy(true)
    try {
      const value = await fn()
      cache.invalidateQueries(['billing'])
      if (success)
        toast({ title: success, status: 'success', isClosable: true })
      return value
    } catch (e) {
      let message = e.response?.data?.message
      if (e.response?.data instanceof Blob) {
        try {
          message = JSON.parse(await e.response.data.text()).message
        } catch (_) {}
      }
      toast({
        title: message || 'No se pudo completar la operación.',
        status: 'error',
        duration: 10000,
        isClosable: true
      })
      return null
    } finally {
      setBusy(false)
    }
  }
  return { busy, action }
}
